import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { listRequirements, approveRequirement, rejectRequirement, type Requirement, type Page } from "../api/delivery.ts";

const STATUS_COLORS: Record<string, string> = {
  DRAFT: "bg-gray-700 text-gray-300",
  IN_REVIEW: "bg-yellow-900/50 text-yellow-300",
  APPROVED: "bg-green-900/50 text-green-300",
  REJECTED: "bg-red-900/50 text-red-300",
  ARCHIVED: "bg-gray-800 text-gray-500",
};

const PRIORITY_COLORS: Record<string, string> = {
  MUST: "text-red-400",
  SHOULD: "text-orange-400",
  COULD: "text-yellow-400",
  WONT: "text-gray-500",
};

export function DeliveryRequirementsPage() {
  const { token } = useAuth();
  const [data, setData] = useState<Page<Requirement> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [acting, setActing] = useState<string | null>(null);

  const load = () => {
    if (token == null) return;
    setLoading(true);
    listRequirements(token, { type: typeFilter || undefined, status: statusFilter || undefined, limit: 100 })
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [token, typeFilter, statusFilter]);

  const act = async (fn: () => Promise<Requirement>) => {
    try {
      setActing("loading");
      await fn();
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setActing(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Requirements</h1>
        </div>

        <div className="flex gap-3">
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="bg-gray-800 border border-gray-700 text-gray-200 rounded px-3 py-1.5 text-sm">
            <option value="">All Types</option>
            {["FUNCTIONAL", "NON_FUNCTIONAL", "USER_STORY", "ACCEPTANCE_CRITERIA"].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-gray-800 border border-gray-700 text-gray-200 rounded px-3 py-1.5 text-sm">
            <option value="">All Statuses</option>
            {["DRAFT", "IN_REVIEW", "APPROVED", "REJECTED"].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {loading && <p className="text-gray-400">Loading…</p>}
        {error != null && <p className="text-red-400">{error}</p>}

        {data != null && (
          <div className="space-y-2">
            <p className="text-gray-400 text-sm">{data.total} requirements</p>
            {data.items.map((req) => (
              <div key={req.id} className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-gray-500 font-mono">{req.type}</span>
                      <span className={`text-xs font-medium ${PRIORITY_COLORS[req.priority] ?? "text-gray-400"}`}>{req.priority}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[req.status] ?? "bg-gray-700 text-gray-300"}`}>{req.status}</span>
                    </div>
                    <p className="text-white font-medium mt-1">{req.title}</p>
                    {req.description && <p className="text-gray-400 text-sm mt-1 line-clamp-2">{req.description}</p>}
                    {req.acceptanceCriteria.length > 0 && (
                      <ul className="mt-2 space-y-0.5">
                        {req.acceptanceCriteria.slice(0, 3).map((ac, i) => <li key={i} className="text-gray-500 text-xs">• {ac}</li>)}
                      </ul>
                    )}
                  </div>
                  {req.status === "IN_REVIEW" && (
                    <div className="flex gap-2 shrink-0">
                      <button disabled={acting != null} onClick={() => act(() => approveRequirement(token!, req.id, req.version))} className="px-3 py-1 text-xs bg-green-700 hover:bg-green-600 text-white rounded transition-colors disabled:opacity-50">Approve</button>
                      <button disabled={acting != null} onClick={() => act(() => rejectRequirement(token!, req.id, req.version))} className="px-3 py-1 text-xs bg-red-700 hover:bg-red-600 text-white rounded transition-colors disabled:opacity-50">Reject</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
